import { type MaterialRef } from '@shared/material'
import { clsx } from 'clsx'
import { BookOpen } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'
import { useMaterial, useRegionCanvas } from './hooks'

const WHOLE = { x: 0, y: 0, w: 1, h: 1 }

/** A place in a material, in text: its name and page; opens the reader there. */
export function MaterialChip({ target, fallback }: { target: MaterialRef; fallback: ReactNode }) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const material = useMaterial(target.materialId)
  const label =
    fallback || (material ? `${material.name} · ${t('clip.page', { n: target.page })}` : '…')
  return (
    <button
      type="button"
      data-mention=""
      disabled={!material}
      title={material ? t('clip.open') : t('material.missing')}
      onClick={() => {
        reader.open(target.materialId, target.page, target.rect)
      }}
      className={clsx(
        'mx-0.5 inline-flex max-w-[16rem] translate-y-[-1px] items-center gap-1 rounded-md px-1.5 align-middle text-[12.5px] leading-5 font-medium transition-colors',
        material
          ? 'bg-ai-soft text-ai hover:bg-ai hover:text-white'
          : 'cursor-default bg-surface-2 text-muted line-through',
      )}
    >
      <BookOpen size={11} className="flex-none opacity-70" />
      <span className="truncate">{label}</span>
    </button>
  )
}

/** Most an embedded region is drawn wide, in CSS pixels. */
const EMBED_W = 520

/** A region of a material shown inside a message, drawn from the PDF; opens the reader there. */
export function MaterialEmbed({ target, caption }: { target: MaterialRef; caption: string }) {
  const { t } = useI18n()
  const { reader } = useBoardCtx()
  const material = useMaterial(target.materialId)
  const rect = target.rect ?? WHOLE
  const [width, setWidth] = useState(EMBED_W)
  const canvas = useRegionCanvas(material, target.page, rect, width)
  const p = material?.pages[target.page - 1]
  const aspect = p ? (rect.h * p.h) / (rect.w * p.w) : 0.6
  if (!material)
    return (
      <div className="my-2 rounded-md border border-dashed border-line px-3 py-2 text-[12.5px] text-muted">
        {t('material.missing')}
      </div>
    )
  return (
    <figure
      className="my-2 overflow-hidden rounded-md border border-line bg-surface"
      ref={(el) => {
        if (el) setWidth(Math.min(EMBED_W, Math.round(el.clientWidth)))
      }}
      style={{ maxWidth: EMBED_W }}
    >
      <div className="bg-white" style={{ aspectRatio: String(1 / aspect), maxHeight: 560 }}>
        <canvas ref={canvas} aria-label={caption} className="block size-full object-contain" />
      </div>
      <figcaption className="flex items-center gap-1.5 border-t border-line px-2 py-1 text-[11.5px] text-muted">
        <span className="min-w-0 flex-1 truncate">{caption || material.name}</span>
        <button
          type="button"
          onClick={() => {
            reader.open(target.materialId, target.page, target.rect)
          }}
          className="inline-flex flex-none items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-surface-2 hover:text-ink"
        >
          <BookOpen size={12} />
          {t('clip.page', { n: target.page })}
        </button>
      </figcaption>
    </figure>
  )
}
